import { Quaternion, Vector3, Vector4 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';
import type { AssistantGlow, SceneRoot } from '../core/SceneRoot';
import type { GrabberHub } from '../input/Grabbers';
import type { MicIndicatorState } from './AssistantController';

/**
 * The single off-hand status glyph, replacing what used to be two separate markers
 * (`MicIndicatorRenderer` for mic state, `AssistantGlowIndicatorRenderer` for the room-wide
 * assistant glow copied onto the hand) — plan/mic-indicator.md, the owner's own framing for
 * why: "That also ensures that we have a single indicator." A person used to learn one object
 * and read it once; two objects beside the same hand meant checking two things and working out
 * how they relate. This class is the merge.
 *
 * A drawn microphone **glyph** (plan/mic-indicator.md — a recognisable mic symbol, not an
 * abstract dot: a capsule body over a curved cradle on a stem, solid-filled), painted onto a
 * `DynamicTexture` from canvas 2D primitives, on a billboarded plane that tracks the off-hand
 * controller. Four looks, one glyph:
 *
 * | State | Look |
 * | --- | --- |
 * | Microphone open | Black, solid |
 * | Gemma is listening *to you* | Glowing blue |
 * | You muted yourself | Red, with a slash across the glyph |
 * | Microphone unavailable | Grey, same slash |
 *
 * The slash means one thing in both slashed states — no audio is leaving this microphone — and
 * the colour says why: red because it was chosen, grey because it was never available to choose
 * (plan/mic-indicator.md, "The state the owner's list does not cover"). Where mic-open and
 * Gemma-listening are both true, listening wins — see `deriveGlyphState` below.
 *
 * **Blue is for this connection's own turn only.** `AssistantGlow` carries `listening` (this
 * connection's own `beginTalk`) and `listening-remote` (someone else's turn, broadcast to
 * `OthersInGroup`) as deliberately distinct values — a two-person headset session reported the
 * bug this distinction fixes: "the Gemma input indicator enables for all players so the channel
 * to her is wide open for everyone," fixed one commit before this class existed (c11a808). This
 * glyph reads only `'listening'`; `'listening-remote'`, `'speaking'` and `'none'` all fall
 * through to the plain microphone state below. The room-wide glow (ceiling light, sky stars,
 * `SceneRoot.ts`) is untouched by this change and keeps showing a remote turn to everyone — that
 * behaviour is correct and stays exactly as it is; only this personal, off-hand object collapses
 * to one glyph.
 *
 * Polls `SceneRoot.assistantGlow` every tick rather than a second `AssistantController` callback
 * — the same "poll, don't add a second event path" shape the renderer this replaces already
 * used, and the reason is unchanged: `onGlowChanged` is a single callback, already claimed by
 * `main.ts`'s own wiring straight to `SceneRoot.setAssistantGlow`, so a poll gets this class the
 * same information without turning that callback into a multi-listener mechanism it has no other
 * reason to be. `setState` (called from `AssistantController.onIndicatorChanged`) is the only
 * push path, carrying the plain mic state alone.
 *
 * Unparented and repositioned from the off-hand `Grabber`'s own pose every tick — "on the
 * controller, never a HUD" (plan/mic-indicator.md), carried over unchanged from the renderer
 * this replaces. What is new is the sideways placement: plan/mic-indicator.md, "On the inside
 * face of the off-hand controller: to the right of a left-hand controller, to the left of a
 * right-hand one" — see `localOffsetFor` below for how that sign is derived, and why it has to be
 * re-read every frame rather than fixed at construction.
 */
export class MicIndicatorRenderer {
  private mesh: Mesh | null = null;
  private material: StandardMaterial | null = null;
  private texture: DynamicTexture | null = null;

  private micState: MicIndicatorState = 'unavailable';

  /** The glyph state actually painted onto the texture, so a tick that resolves to the same
   *  state as last time skips the redraw — the same "one mesh, re-textured, skip when nothing
   *  changed" rule `ControlHintsPanel`/`LabelRenderer` already use for their own `DynamicTexture`
   *  plates. Also the seam a test reads to prove the state machine without a rendered pixel
   *  (`NullEngine` cannot produce one — CLAUDE.md). */
  private lastPainted: MicGlyphState | null = null;

  /** How many times the texture was actually repainted — exposed only so a test can prove the
   *  skip-when-unchanged behaviour above is load-bearing rather than assumed. */
  private repaints = 0;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
    private readonly sceneRoot: SceneRoot,
  ) {}

  /** Called by `AssistantController.onIndicatorChanged`. */
  setState(state: MicIndicatorState): void {
    this.micState = state;
  }

  /** The glyph state currently painted — for tests only (see `lastPainted`'s own doc). */
  get renderedState(): MicGlyphState | null {
    return this.lastPainted;
  }

  /** How many real repaints have happened — for tests only (see `repaints`'s own doc). */
  get repaintCount(): number {
    return this.repaints;
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick());
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  private tick(): void {
    const offHand = this.grabbers.byRole('off');

    // Controllers only: X/Y (and therefore both the mic toggle and a held assistant turn) do
    // not exist for a tracked hand or the desktop pointer, so showing this glyph there would
    // claim a capability that is not actually reachable — the identical restriction the renderer
    // this replaces already applied.
    if (!offHand || offHand.kind !== 'controller') {
      this.hide();
      return;
    }

    const glyphState = deriveGlyphState(this.micState, this.sceneRoot.assistantGlow);
    const mesh = this.ensureMesh();
    this.paintIfChanged(glyphState);

    const offset = localOffsetFor(offHand.handedness).applyRotationQuaternion(offHand.rotation);
    mesh.position.copyFrom(offHand.position.add(offset));

    // Yaw-billboarded toward the eye — CreatePlane's front is -Z (CLAUDE.md), so the yaw is
    // atan2(-x, -z), not atan2(x, z); the same construction ControlHintsPanel/LabelRenderer/
    // ModeRingRenderer already use. Falls back to identity with no active camera, the same
    // ModeRingRenderer choice: visibility and position must not depend on a camera existing,
    // only orientation does, so a headless test with no camera constructed is still a
    // legitimate way to prove placement without also proving billboard orientation in the same
    // assertion. Unlike the sphere marker this replaces, the glyph is a flat drawing and would
    // read edge-on or mirrored if it only followed the controller's own rotation the way the
    // position offset does — billboarding keeps it legible at whatever angle the hand is held.
    const camera = this.scene.activeCamera;
    const rotation = camera
      ? Quaternion.FromEulerAngles(
          0,
          Math.atan2(-(camera.globalPosition.x - mesh.position.x), -(camera.globalPosition.z - mesh.position.z)),
          0,
        )
      : Quaternion.Identity();
    (mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(rotation);

    mesh.setEnabled(true);
  }

  private paintIfChanged(state: MicGlyphState): void {
    if (state === this.lastPainted) {
      return;
    }

    this.lastPainted = state;
    this.repaints += 1;

    const texture = this.texture;

    if (!texture) {
      return;
    }

    const context = as2d(texture.getContext());
    paintMicGlyph(context, TEXTURE_WIDTH, TEXTURE_HEIGHT, lookFor(state));
    texture.update();
  }

  private ensureMesh(): Mesh {
    if (this.mesh && this.material && this.texture) {
      return this.mesh;
    }

    const mesh = CreatePlane(
      'mic-indicator',
      {
        width: GLYPH_WIDTH,
        height: GLYPH_HEIGHT,
        sideOrientation: DOUBLE_SIDED,
        frontUVs: new Vector4(0, 0, 1, 1),
        backUVs: new Vector4(1, 0, 0, 1),
      },
      this.scene,
    );
    mesh.isPickable = false;
    mesh.rotationQuaternion = Quaternion.Identity();

    const texture = new DynamicTexture(
      'mic-indicator-texture',
      { width: TEXTURE_WIDTH, height: TEXTURE_HEIGHT },
      this.scene,
      true,
    );
    texture.hasAlpha = true;

    // Unlit, texture-driven material — the same construction `ControlHintsPanel`/
    // `LabelledPlate.buildPlateMesh` use for their own drawn plates: the painted colour is what
    // renders regardless of room lighting, which is exactly what a status glyph needs (a red
    // "muted" glyph must not read as some other colour standing in a dim corner).
    const material = new StandardMaterial('mic-indicator-material', this.scene);
    material.diffuseTexture = texture;
    material.useAlphaFromDiffuseTexture = true;
    material.emissiveTexture = texture;
    material.disableLighting = true;
    material.specularColor = Color3.Black();
    // `backFaceCulling` deliberately left at the material default (`true`) — CLAUDE.md,
    // "DOUBLESIDE plus disabled backFaceCulling is a second, independent way to ship the same
    // mirrored-text bug." DOUBLESIDE alone (frontUVs/backUVs above) is what makes the back face
    // readable rather than blank; culling stays on so only one of the two coincident triangle
    // sets ever rasterizes.
    mesh.material = material;

    this.mesh = mesh;
    this.material = material;
    this.texture = texture;

    // A mesh built after a state was already set (the common case: AssistantController reports
    // 'unavailable' or 'muted' before any controller ever appears) must not show a blank canvas
    // once it finally does — repaint immediately rather than waiting for the next state change.
    this.lastPainted = null;
    this.paintIfChanged(deriveGlyphState(this.micState, this.sceneRoot.assistantGlow));

    return mesh;
  }

  private hide(): void {
    this.mesh?.setEnabled(false);
  }

  dispose(): void {
    // Explicit texture dispose before the material, not left to the material's own default —
    // CLAUDE.md: `Material.dispose()`'s `forceDisposeTextures` argument defaults to `false`, so
    // a plain `material.dispose()` would leave this texture alive and unreachable. The identical
    // ordering `LabelledPlate.disposePlate` already uses. One `DynamicTexture` instance is
    // assigned to both `diffuseTexture` and `emissiveTexture` above, so one dispose call covers
    // both slots.
    this.texture?.dispose();
    this.material?.dispose();
    this.mesh?.dispose();
    this.texture = null;
    this.material = null;
    this.mesh = null;
    this.lastPainted = null;
  }
}

/** The combined visual state this glyph actually paints — plan/mic-indicator.md's four rows,
 *  named for what each one shows rather than for the mic/glow inputs that produce it. */
export type MicGlyphState = 'open' | 'listening' | 'muted' | 'unavailable';

/**
 * Merges the plain microphone state with the room's assistant glow into the one glyph state
 * plan/mic-indicator.md specifies. Pure and Babylon-free on purpose, the same "state machine
 * separate from the renderer" split `AssistantController` itself keeps — this function is what a
 * test asserts the priority rule against, with no `NullEngine` or mesh involved at all.
 *
 * `glow === 'listening'` always wins, regardless of the mic state it would otherwise show
 * (plan/mic-indicator.md: "Where mic-open and Gemma-listening are both true, listening wins — it
 * already implies the mic is open"). In practice `'listening'` is only ever set while this
 * connection's own push-to-talk turn is open, which already forces the underlying mic state to
 * `'live'` (`AssistantController.reportIndicator`) — the two are not expected to disagree — but
 * this function does not lean on that invariant holding; if they ever did disagree, showing that
 * Gemma is listening is the more useful fact of the two to surface.
 *
 * `'listening-remote'`, `'speaking'` and `'none'` are deliberately absent from the branch below:
 * they fall straight through to the plain mic state. `'listening-remote'` is the one that
 * matters most to get right — see this file's own module doc for the bug that distinction fixes.
 */
export function deriveGlyphState(mic: MicIndicatorState, glow: AssistantGlow): MicGlyphState {
  if (glow === 'listening') {
    return 'listening';
  }

  switch (mic) {
    case 'live':
      return 'open';
    case 'muted':
      return 'muted';
    case 'unavailable':
      return 'unavailable';
  }
}

/** Physical size in metres — legible at arm's length, roughly three times the previous sphere
 *  marker's own diameter (16mm), which was reported from a headset as too small
 *  (plan/mic-indicator.md). Portrait, matching a mic glyph's own proportions (taller than wide). */
const GLYPH_WIDTH = 0.045;
const GLYPH_HEIGHT = 0.06;

/** Texture resolution — same aspect ratio as the physical plate, the same derivation
 *  `ControlHintsPanel`/`LabelledPlate` use for their own `DynamicTexture`s. */
const TEXTURE_WIDTH = 160;
const TEXTURE_HEIGHT = Math.round((GLYPH_HEIGHT / GLYPH_WIDTH) * TEXTURE_WIDTH);

/** `Mesh.DOUBLESIDE`, inlined so this module needs no value import of `Mesh` — the same trick
 *  `LabelledPlate.ts`/`ControlHintsPanel.ts` both already use. */
const DOUBLE_SIDED = 2;

/** How far above the grip and forward of it the glyph floats, in metres — clear of the
 *  controller model itself without drifting into "floating nearby" territory. Unchanged in
 *  spirit from the sphere marker this replaces (`0.03` up, `0.02` forward); the sideways
 *  component is new — see `localOffsetFor`.
 *
 *  `SIDE_OFFSET` was `0.045` until the owner's 2026-08-13 headset pass: "Mic glyph needs to move
 *  a bit further away from the controller, it clips a bit. 2cm should do it." Only this constant
 *  moved (+0.02) — `UP_OFFSET`/`FORWARD_OFFSET` are untouched, and so is the sign derivation in
 *  `localOffsetFor` below, which still re-reads the physical hand every tick. */
const UP_OFFSET = 0.03;
const FORWARD_OFFSET = 0.02;
const SIDE_OFFSET = 0.065;

/**
 * The off-hand glyph's local offset, signed by the off hand's own *physical* handedness so it
 * sits on the controller's **inside face** — plan/mic-indicator.md: "to the right of a left-hand
 * controller, to the left of a right-hand one." Called fresh every tick from the live grabber's
 * own `handedness` field (not cached), so a mid-session handedness flip (`Handedness.ts`) moves
 * the glyph to the other side of the hand the next frame, with no reload — the same "re-derived
 * per frame" rule every other handedness-dependent read in this project already follows.
 *
 * The sign is derived from two facts, not assumed:
 *
 * 1. **The WebXR grip-space X axis convention.** Per the WebXR Device API spec (mirrored on MDN,
 *    `XRInputSource.gripSpace`): "the x-axis is perpendicular to the palm of the user's hand,
 *    with the direction extending outward from the back of the hand being +X if the controller
 *    is in the user's right hand or -X if the controller is in the left hand." Read the other
 *    way around: **local +X points inward (toward the body's centreline) for a left-hand
 *    controller**, and outward for a right-hand one — the left hand's own "outward" is -X, so
 *    its +X is whatever is left over, which is inward.
 * 2. **Babylon's WebXR-to-scene conversion leaves that meaning intact.** This project's scene is
 *    left-handed (`useRightHandedSystem` unset); confirmed by reading Babylon's own
 *    `WebXRInputSource.updateFromXRFrame` (`@babylonjs/core/XR/webXRInputSource.js`), the
 *    conversion for a left-handed scene is exactly `grip.position.z *= -1;
 *    grip.rotationQuaternion.z *= -1; grip.rotationQuaternion.w *= -1;` — nothing touches the
 *    quaternion's `x`/`y` components. Expanding the quaternion-to-matrix formula shows this is
 *    precisely conjugation by the Z-mirror `diag(1, 1, -1)`: every matrix entry that touches the
 *    Z axis flips sign, every entry that does not (including the local X axis's own X and Y
 *    world-space components — the ones that decide whether it reads as "left" or "right") stays
 *    unchanged. So Babylon's own `grip.rotationQuaternion` (what `Grabber.rotation` copies)
 *    carries the identical inward/outward meaning as the raw WebXR grip space above.
 *
 * Put together: **inward is local +X for a left-hand controller, local -X for a right-hand
 * one.** `Grabbers.test.ts`'s own `aimRotationFromForward` cases are the precedent for pinning a
 * sign this way in this codebase — a documented derivation, not a guess — and this project has
 * no rendered pixel to check it against instead (`NullEngine` never rasterizes); the contact
 * sheet this change ships alongside shows the glyph's *shape*, not which physical side it sits
 * on, so this derivation is the only proof available for the offset's sign until a headset pass
 * confirms it.
 */
function localOffsetFor(physicalHand: XRHandedness): Vector3 {
  const inward = physicalHand === 'left' ? 1 : -1;
  return new Vector3(inward * SIDE_OFFSET, UP_OFFSET, FORWARD_OFFSET);
}

/** The rendered look for one glyph state: fill colour, whether the "no audio leaving" slash is
 *  drawn, whether the glyph gets a soft glow (`listening` only — "Glowing blue"), and whether it
 *  gets a thin light halo (see `HALO_COLOR`'s own doc comment). Colours match the sphere
 *  marker's own palette where a state carries over (`muted` reuses the old `live`/recording-red,
 *  `unavailable` reuses the old `muted` grey) so the change in *shape* (sphere to glyph) is not
 *  also an unrelated change in what red/grey mean elsewhere in the project. */
interface GlyphLook {
  readonly color: string;
  readonly slashed: boolean;
  readonly glow: boolean;
  readonly haloed: boolean;
}

const OPEN_COLOR = '#000000';
const LISTENING_COLOR = '#4D73F2';
const MUTED_COLOR = '#E61A1A';
const UNAVAILABLE_COLOR = '#80858F';

/**
 * `open` is literal `#000000` per the owner's own spec, and stays that colour — but PR #141
 * (tinted-glass walls and floor) made the room's dark starfield sky visible *through* the room
 * itself for the first time, and a black glyph against that dark space can disappear entirely.
 * `unavailable`'s mid-grey is the other plausible casualty at a glance across a dim room.
 * `listening` (glowing blue) and `muted` (vivid red) are both already bright enough, against any
 * background this room can show, that they do not get this treatment.
 */
function lookFor(state: MicGlyphState): GlyphLook {
  switch (state) {
    case 'open':
      return { color: OPEN_COLOR, slashed: false, glow: false, haloed: true };
    case 'listening':
      return { color: LISTENING_COLOR, slashed: false, glow: true, haloed: false };
    case 'muted':
      return { color: MUTED_COLOR, slashed: true, glow: false, haloed: false };
    case 'unavailable':
      return { color: UNAVAILABLE_COLOR, slashed: true, glow: false, haloed: true };
  }
}

/**
 * Paints the microphone glyph: a rounded capsule body, a curved cradle beneath it, a stem and a
 * base bar — plan/mic-indicator.md's own description of the owner's reference image, drawn from
 * canvas 2D primitives rather than loaded from a file ("Just draw it for now" — the owner's own
 * call; swapping in a real asset later is a texture-source change confined to this one function).
 * Exported so the standalone contact-sheet script (`tools/mic-glyph/render-mic-glyph-preview.mjs`)
 * can be visually compared against this exact function's own output rather than a hand-copied
 * reimplementation drifting from it — see that script's own header for why a *second*,
 * independent SVG rendering exists at all (a human has to look at the result; `NullEngine` never
 * rasterizes — CLAUDE.md).
 */
export function paintMicGlyph(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  look: GlyphLook,
): void {
  context.clearRect(0, 0, width, height);

  if (look.glow) {
    context.shadowColor = look.color;
    context.shadowBlur = width * 0.35;
  } else {
    context.shadowBlur = 0;
  }

  context.lineCap = 'round';

  const capsuleWidth = width * CAPSULE_WIDTH_FRACTION;
  const capsuleHeight = height * CAPSULE_HEIGHT_FRACTION;
  const capsuleLeft = (width - capsuleWidth) / 2;
  const capsuleTop = height * CAPSULE_TOP_FRACTION;

  const centerX = width / 2;
  const cradleRadius = capsuleWidth / 2 + width * CRADLE_EXTRA_RADIUS_FRACTION;
  const cradleCenterY = capsuleTop + capsuleHeight * CRADLE_CENTER_FRACTION;
  const strokeWidth = width * STROKE_WIDTH_FRACTION;
  const haloWidth = strokeWidth + width * HALO_EXTRA_WIDTH_FRACTION * 2;

  // Capsule body — filled, with a wider halo stroke laid down first (haloed states only) so a
  // thin rim of HALO_COLOR survives just outside the fill's own edge. See HALO_COLOR's own doc
  // comment for why this exists and which states get it.
  traceCapsule(context, capsuleLeft, capsuleTop, capsuleWidth, capsuleHeight);
  if (look.haloed) {
    context.lineWidth = haloWidth;
    context.strokeStyle = HALO_COLOR;
    context.stroke();
  }
  context.fillStyle = look.color;
  context.fill();

  // Cradle, stem, base bar — each stroked the same halo-then-colour way via strokeHaloed.
  //
  // The cradle is the bottom half of the circle — arc(cx, cy, r, 0, PI) sweeps through theta =
  // PI/2, i.e. (centerX, cradleCenterY + cradleRadius), the lowest point of the circle, since
  // canvas angles increase clockwise in this y-down coordinate system (theta = PI/2 lands on
  // POSITIVE y, further down the canvas, not up). That is the "U" shape a cradle actually needs:
  // both endpoints (the arms' tips) sit level with cradleCenterY and rise no further, while the
  // curve dips DOWN to its lowest point, which is exactly where the stem below picks up (see its
  // own moveTo). The previous arc(cx, cy, r, PI, 2*PI) swept the *top* half instead — endpoints
  // level with cradleCenterY, peak rising ABOVE it — which drew a dome across the capsule's own
  // silhouette rather than a bracket beneath it, and left a gap to the stem below (the old
  // "PI to 2*PI draws through the bottom" comment here was simply wrong; verified by walking the
  // parametrisation, not just eyeballing the render).
  context.beginPath();
  context.arc(centerX, cradleCenterY, cradleRadius, 0, Math.PI);
  strokeHaloed(context, look, strokeWidth, haloWidth);

  const stemBottomY = height * STEM_BOTTOM_FRACTION;
  context.beginPath();
  context.moveTo(centerX, cradleCenterY + cradleRadius);
  context.lineTo(centerX, stemBottomY);
  strokeHaloed(context, look, strokeWidth, haloWidth);

  const baseY = height * BASE_Y_FRACTION;
  const baseHalfWidth = width * BASE_HALF_WIDTH_FRACTION;
  context.beginPath();
  context.moveTo(centerX - baseHalfWidth, baseY);
  context.lineTo(centerX + baseHalfWidth, baseY);
  strokeHaloed(context, look, strokeWidth, haloWidth);

  context.shadowBlur = 0;

  if (look.slashed) {
    const slashWidth = width * SLASH_WIDTH_FRACTION;
    // The slash's own full painted footprint — wider than SLASH_WIDTH_FRACTION alone when this
    // look is haloed, since the halo pass extends HALO_EXTRA_WIDTH_FRACTION past each edge of
    // the normal-width stroke. The knockout gap below has to clear *this*, not the bare slash
    // width, or a haloed slash's own halo exactly refills the gap it was cut for (found by
    // rendering the contact sheet: 'unavailable' — slashed and haloed — showed no visible gap at
    // all, unlike 'muted', because its halo pass alone was already as wide as the gap).
    const slashHaloWidth = slashWidth + width * HALO_EXTRA_WIDTH_FRACTION * 2;
    const slashFootprint = look.haloed ? slashHaloWidth : slashWidth;
    const gapWidth = slashFootprint + width * SLASH_GAP_MARGIN_FRACTION * 2;
    const x1 = width * SLASH_X1_FRACTION;
    const y1 = height * SLASH_Y1_FRACTION;
    const x2 = width * SLASH_X2_FRACTION;
    const y2 = height * SLASH_Y2_FRACTION;

    // Knockout gap first: a wide destination-out stroke along the slash line, cutting a
    // transparent gap through the glyph body already painted above — the fix for "the slash and
    // the microphone merge into a single blob" (owner's report against the reference image,
    // which shows the slash clearly separated from the mic by a gap, reading as a bar lying
    // *over* the symbol rather than through it). Wide enough that a clear ring of transparency
    // survives on both sides of the visible slash (and its own halo, if haloed) drawn next.
    context.save();
    context.globalCompositeOperation = 'destination-out';
    context.lineWidth = gapWidth;
    context.beginPath();
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    context.stroke();
    context.restore();

    context.beginPath();
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    strokeHaloed(context, look, slashWidth, slashHaloWidth);
  }
}

/** Strokes the *current* path twice for a haloed look: a wider pass in `HALO_COLOR` first, then
 *  the normal-width pass in the glyph's own colour directly on top of the same path — `stroke()`
 *  does not consume or clear the current path, so both calls draw the identical geometry and
 *  only the outer rim of the first pass ends up visible, as a thin light outline. A non-haloed
 *  look just runs the second half, so every stroke in `paintMicGlyph` can go through this one
 *  helper regardless of state. */
function strokeHaloed(context: CanvasRenderingContext2D, look: GlyphLook, lineWidth: number, haloWidth: number): void {
  if (look.haloed) {
    context.lineWidth = haloWidth;
    context.strokeStyle = HALO_COLOR;
    context.stroke();
  }
  context.lineWidth = lineWidth;
  context.strokeStyle = look.color;
  context.stroke();
}

/** Traces a stadium/capsule path — the same `arcTo`-based rounded-rect construction
 *  `LabelledPlate.tracePlatePath`/`ControlHintsPanel.tracePanelPath` use, with the radius fixed
 *  to exactly half the width so the top and bottom edges become full semicircles rather than
 *  merely rounded corners. Not imported from either of those modules: both radii are
 *  module-private, and this shape (radius always `width / 2`) is a different call from either
 *  file's own fixed-or-fractional corner radius. `arcTo` over the newer `roundRect`, the same
 *  reason those two files give: this has to run against a headless canvas mock in tests, not
 *  only a real browser. */
function traceCapsule(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
  const radius = Math.min(width / 2, height / 2);

  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.arcTo(x + width, y, x + width, y + radius, radius);
  context.lineTo(x + width, y + height - radius);
  context.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  context.lineTo(x + radius, y + height);
  context.arcTo(x, y + height, x, y + height - radius, radius);
  context.lineTo(x, y + radius);
  context.arcTo(x, y, x + radius, y, radius);
  context.closePath();
}

/** Glyph layout, as fractions of the texture's own width/height so the drawing scales cleanly
 *  with `TEXTURE_WIDTH`/`TEXTURE_HEIGHT` — tuned by eye against the rendered contact sheet
 *  (`mic-glyph-preview.png`), not derived from a formula. Mirrored, by value, in
 *  `tools/mic-glyph/render-mic-glyph-preview.mjs`'s own SVG generator — see that script's header
 *  for why a hand-kept copy is an acceptable amount of duplication for a one-off preview tool.
 *
 *  `CRADLE_CENTER_FRACTION` is expressed as a fraction of the *capsule's own* height
 *  (`cradleCenterY = capsuleTop + capsuleHeight * CRADLE_CENTER_FRACTION`), which couples the
 *  two shapes: for any fraction below 1, the cradle's tips sit at a height that is always
 *  strictly between the capsule's own top and bottom, by construction, regardless of how tall or
 *  where the capsule itself is. That coupling is why the owner's 2026-08-13 report — "the
 *  microphone head part just needs to be a bit longer, and sit a tiny bit higher, so it doesn't
 *  merge into the arm" — could not be satisfied by moving the capsule alone: enlarging just
 *  `CAPSULE_HEIGHT_FRACTION`/`CAPSULE_TOP_FRACTION` (tried first, against the rendered sheet)
 *  left the fraction the arc's tips sit at relative to the capsule unchanged, so the cradle's own
 *  dip sank *further* into the capsule's silhouette as it grew, worsening the merge instead of
 *  fixing it. `CRADLE_CENTER_FRACTION` moved from `0.42` to `0.75` to push the cradle down far
 *  enough, relative to the now-taller capsule, that the arc clears the capsule's rounded bottom
 *  entirely — confirmed against the rendered sheet, not derived, per this comment's own opening
 *  line. */
const CAPSULE_WIDTH_FRACTION = 0.34;
const CAPSULE_HEIGHT_FRACTION = 0.4;
const CAPSULE_TOP_FRACTION = 0.05;
const CRADLE_EXTRA_RADIUS_FRACTION = 0.13;
const CRADLE_CENTER_FRACTION = 0.75;
const STROKE_WIDTH_FRACTION = 0.085;
const STEM_BOTTOM_FRACTION = 0.84;
const BASE_Y_FRACTION = 0.87;
const BASE_HALF_WIDTH_FRACTION = 0.15;
const SLASH_WIDTH_FRACTION = 0.12;
const SLASH_X1_FRACTION = 0.14;
const SLASH_Y1_FRACTION = 0.12;
const SLASH_X2_FRACTION = 0.86;
const SLASH_Y2_FRACTION = 0.9;

/**
 * Colour for the thin light outline a haloed state's glyph (see `GlyphLook.haloed`/`lookFor`) is
 * drawn with — a fixed near-white regardless of the glyph's own colour, so a black or grey glyph
 * stays legible against the room's own dark starfield sky, which PR #141's semi-transparent
 * walls and floor made visible behind this glyph for the first time. A consistent halo colour
 * reads as "this glyph has an outline," rather than the state's own colour merely looking
 * lightened, which a tint of `look.color` itself would risk instead.
 */
const HALO_COLOR = '#F2F4F8';

/** How much wider than the element's own stroke or fill-outline width the halo pass is, split
 *  evenly on both sides — the capsule's filled outline and every stroked element (cradle, stem,
 *  base bar, slash) all go through the same halo-then-colour construction (`strokeHaloed`), so
 *  one constant governs the rim's thickness everywhere it appears. */
const HALO_EXTRA_WIDTH_FRACTION = 0.05;

/** Clearance the `destination-out` knockout gap leaves beyond the slash's own painted footprint,
 *  on each side — see `paintMicGlyph`'s own comment at the call site for why the footprint it is
 *  measured from is the slash's *full* width including its own halo pass when `look.haloed` is
 *  true, not `SLASH_WIDTH_FRACTION` alone: a fixed gap width sized only for the bare slash came
 *  out numerically equal to a haloed slash's own halo width, so the halo silently refilled the
 *  entire gap and 'unavailable' (slashed and haloed) showed no visible separation at all, caught
 *  only by rendering the contact sheet and comparing it against 'muted' (slashed, not haloed),
 *  which showed a clear gap under the exact same code path. */
const SLASH_GAP_MARGIN_FRACTION = 0.05;
